import { Path, Vector2 } from "three";
import React from "react";
import { MiniOrbitControls } from "./MiniOrbitControls";
import classNames from "classnames";
import bezier from "cubic-bezier";
import { MouseTutorialStepEngine } from "./MouseTutorialStepEngine";

const resolution = 200;
const radius = 100;
const easeInOutSine = bezier(0.445, 0.05, 0.55, 0.95, 1e-5);

export const ellipse = new Path().ellipse(
  0,
  0,
  radius,
  radius,
  0,
  (360 * Math.PI) / 180,
  false,
  (90 * Math.PI) / 180
);
export const horizontalLine = new Path().ellipse(
  0,
  0,
  0,
  radius,
  0,
  (360 * Math.PI) / 180,
  false,
  (90 * Math.PI) / 180
);

export class MouseAvatar extends React.Component {
  mouseDivRef = React.createRef<HTMLDivElement>();
  miniOrbitControls = new MiniOrbitControls();
  divOriginalRect: DOMRect;
  declare props: {
    stepEngine: MouseTutorialStepEngine;
    className: string;
  };

  componentDidMount() {
    const mouseDiv = this.mouseDivRef.current as HTMLElement;
    if (mouseDiv) {
      this.divOriginalRect = mouseDiv.getBoundingClientRect();
    }
  }

  get stepEngine() {
    return this.props.stepEngine;
  }

  get step() {
    return this.stepEngine.state.step;
  }

  get mouseAnimation() {
    return this.step.mouseAnimation;
  }

  async waitOrExit(duration: number) {
    return await this.stepEngine.waitOrExit(duration);
  }

  async _genericAction(inner: Function) {
    const mouseDiv = this.mouseDivRef.current as HTMLDivElement;
    const mouseAnimation = this.mouseAnimation;
    mouseAnimation.className && mouseDiv.classList.add(mouseAnimation.className);
    await this.waitOrExit(mouseAnimation.waitBegin || 0);
    this.miniOrbitControls.reset();
    for (let i = 0; i < (mouseAnimation.repeat || 1); i++) {
      await inner();
    }
    this.miniOrbitControls.reset();
    await this.waitOrExit(mouseAnimation.waitEnd || 0);
    mouseAnimation.className && mouseDiv.classList.remove(mouseAnimation.className);
  }

  async rotateAction(path: any, duration: number) {
    await this._genericAction(async () => {
      const mouseAnimation = this.mouseAnimation;
      const pointZero = path.getPoint(0).clone();
      const f = (0.08 * 2 * Math.PI) / (2 * radius);
      for (let i = 0; i < resolution; i++) {
        const t = easeInOutSine(i / resolution);
        const point = path.getPoint(t);
        const delta = point.sub(pointZero);
        this.miniOrbitControls.rotate(f * delta.x, f * delta.y);
        this.moveDiv(delta);
        await this.waitOrExit(duration / resolution);
      }
    });
  }

  async translate(path: any, duration: number) {
    await this._genericAction(async () => {
      const mouseAnimation = this.mouseAnimation;
      const pointZero = path.getPoint(0).clone();
      const f = 0.03;
      for (let i = 0; i < resolution; i++) {
        const t = easeInOutSine(i / resolution);
        const point = path.getPoint(t);
        const delta = point.sub(pointZero);
        this.miniOrbitControls.translate(f * delta.x, f * delta.y);
        this.moveDiv(delta);
        await this.waitOrExit(duration / resolution);
      }
    });
  }

  async zoomAction(zoom: number, duration: number) {
    await this._genericAction(async () => {
      for (let i = 0; i < resolution; i++) {
        const t = easeInOutSine(i / resolution);
        this.miniOrbitControls.zoom(t * zoom + (1 - t));
        await this.waitOrExit(duration / resolution);
      }
    });
  }

  moveDiv(point: Vector2) {
    const mouseDiv = this.mouseDivRef.current;
    mouseDiv.style.transform = `translate3d(${point.x}px,${point.y}px,0px)`;
  }

  async moveMouseTo(x: number, y: number, duration: number) {
    const mouseDiv = this.mouseDivRef.current;
    const rect = mouseDiv.getBoundingClientRect();
    const path = new Path();
    path.moveTo(rect.x - this.divOriginalRect.x, rect.y - this.divOriginalRect.y);
    path.lineTo(x - this.divOriginalRect.x, y - this.divOriginalRect.y);
    for (let i = 0; i < resolution; i++) {
      const t = easeInOutSine(i / resolution);
      const point = path.getPoint(t);
      this.moveDiv(point);
      await this.waitOrExit(duration / resolution);
    }
  }

  userTakesControlAction = (resolved: (message?: any) => void) => {
    const element = document.body;
    const events = ["pointerdown", "wheel"];
    const cleanup = () => events.forEach((event) => element.removeEventListener(event, callback));
    const callback = (event) => {
      cleanup();
      resolved();
    };
    events.forEach((event) => element.addEventListener(event, callback));
    return cleanup;
  };

  orbitControlsSolvedAction = (
    lookForStates: any[],
    eventCount: number,
    resolved: (message?: any) => void
  ) => {
    const controls = APP.controls;
    let changeCounter = 0;
    const changeListener = () => {
      if (lookForStates.includes(controls.state)) {
        changeCounter++;
      }
      if (changeCounter > eventCount) {
        end();
      }
    };
    const cleanup = () => controls.removeEventListener("change", changeListener);
    const end = () => {
      cleanup();
      resolved();
    };
    controls.addEventListener("change", changeListener);
    return cleanup;
  };

  render() {
    return (
      <div
        ref={this.mouseDivRef}
        className={classNames(
          "mouse",
          "fade-slow",
          this.mouseAnimation?.className,
          this.props.className
        )}
      ></div>
    );
  }
}
